import { inject, Injectable, Injector } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { distinctUntilChanged, filter, fromEvent, map } from 'rxjs';
import {
  MouseButton,
  MouseEventService,
} from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { SideViewCamerasService } from '../../render/cameras/side-view-cameras.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { AbstractEditPanelDragHandler } from './drag-handlers/abstract-edit-panel-drag-handler';
import {
  getContainerPoint,
  getCuboidNdcBoundingBox,
  isPointInsideBox,
} from './drag-handlers/drag-handler-utils';
import {
  FoiSelectorHandler,
  getCuboidFaceFoiSelector,
} from './drag-handlers/foi-selection-handler';
import { ResizeDragHandler } from './drag-handlers/resize-drag-handler';
import {
  getEdgeTolerance,
  isOverCorner,
  isOverEdge,
} from './drag-handlers/resize-utils';
import {
  isOverRotation,
  RotationDragHandler,
} from './drag-handlers/rotation-drag-handler';
import {
  isOverTranslate,
  TranslationDragHandler,
} from './drag-handlers/translation-drag-handler';
import { Axis } from './edit-panel.component';

@Injectable()
export class EditPanelService extends InjectableSuperclass {
  private readonly injector = inject(Injector);
  private readonly store = inject(PointCloudContentStore);
  private readonly shellConnector = inject(shellConnectorToken);
  private readonly mouseEventService = inject(MouseEventService);
  private readonly editPanelCamerasService = inject(SideViewCamerasService);

  private axis?: Axis;
  private dragHandler?: AbstractEditPanelDragHandler;

  constructor() {
    super();

    this.subscribeToDrags();
    this.subscribeToFoiSelection();
    this.subscribeToFoiHighlight();
  }

  setAxis(axis: Axis) {
    this.axis = axis;
  }

  private subscribeToDrags() {
    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'mousedown', {
        modifiers: 'any',
      }),
      ({ event }) => this.handleMouseDown(event),
    );

    this.subscribeTo(
      // Listen to mouse ups for the whole document, so we always keep a safe
      // inner state, stopping ongoing drag listeners before starting new ones
      fromEvent<MouseEvent>(document, 'mouseup').pipe(
        filter(() => isDefined(this.dragHandler)),
      ),
      this.handleMouseUp,
    );
    this.subscribeToContextMenu();
    this.subscribeToCleanUpOnShapeChange();
  }

  private handleMouseDown(event: MouseEvent) {
    this.dragHandler = this.getDragHandler(event);
    if (!this.dragHandler) {
      return;
    }

    assertDefined(this.axis);
    this.store('render')('currentEditSource').set({
      scene: 'side view',
      index: this.axis,
    });
  }

  private handleMouseUp() {
    this.cleanUp();
  }

  private subscribeToContextMenu() {
    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'mousedown', {
        button: MouseButton.Right,
      }),
      ({ event }) => {
        const containerPoint = getContainerPoint(event);
        const focusedCuboidBoundingBox = getCuboidNdcBoundingBox(
          this.store.getFocusedShapeLocation(),
          this.getCamera(),
        );

        if (isPointInsideBox(containerPoint, focusedCuboidBoundingBox)) {
          this.shellConnector.openShapeContextMenu([
            event.clientX,
            event.clientY,
          ]);
        } else {
          this.shellConnector.openCanvasContextMenu([
            event.clientX,
            event.clientY,
          ]);
        }
      },
    );
  }

  private subscribeToFoiSelection() {
    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'click'),
      ({ event }) => {
        if (this.store.state().render.editMode !== 'select face') {
          return;
        }

        const face = this.getFoiSelectorUnderMouse(event);
        if (isDefined(face)) {
          assertDefined(this.axis);
          const handler = new FoiSelectorHandler(this.axis, this.injector);
          handler.updateCuboidFace(face);
        }
      },
    );
  }

  private subscribeToFoiHighlight() {
    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'mouseout'),
      () => this.store('editPanel')('highlightedFoiSelector').delete(),
    );

    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'mousemove'),
      ({ event }) => {
        if (this.store.state().render.editMode === 'select face') {
          const face = this.getFoiSelectorUnderMouse(event);
          this.store('editPanel')('highlightedFoiSelector').set(face);
        } else {
          this.store('editPanel')('highlightedFoiSelector').delete();
        }
      },
    );
  }

  private subscribeToCleanUpOnShapeChange() {
    // Supports tabbing or selection change while performing an action
    this.subscribeTo(
      this.store.focusedShape$.pipe(
        map((shape) => shape?.index),
        distinctUntilChanged(),
      ),
      this.cleanUp,
    );
  }

  private cleanUp() {
    this.dragHandler?.complete();
    this.dragHandler = undefined;
    this.store('render')('currentEditSource').delete();
    this.store('editPanel')('highlightedFoiSelector').delete();
  }

  private getFoiSelectorUnderMouse(event: MouseEvent) {
    const containerPoint = getContainerPoint(event);
    const focusedCuboidBoundingBox = getCuboidNdcBoundingBox(
      this.store.getFocusedShapeLocation(),
      this.getCamera(),
    );
    assertDefined(this.axis);

    const face = getCuboidFaceFoiSelector(
      containerPoint,
      focusedCuboidBoundingBox,
      this.axis,
    );
    return face;
  }

  private getDragHandler(event: MouseEvent) {
    const containerPoint = getContainerPoint(event);
    const focusedCuboidBoundingBox = getCuboidNdcBoundingBox(
      this.store.getFocusedShapeLocation(),
      this.getCamera(),
    );
    assertDefined(this.axis);
    const viewRegion = this.store.getViewRegionStore(this.axis).state();
    const tolerance = getEdgeTolerance(viewRegion, this.getCamera());
    if (isOverCorner(containerPoint, focusedCuboidBoundingBox, tolerance)) {
      return new ResizeDragHandler(this.axis, this.injector, event);
    } else if (
      isOverEdge(containerPoint, focusedCuboidBoundingBox, tolerance)
    ) {
      return new ResizeDragHandler(this.axis, this.injector, event);
    } else if (isOverTranslate(containerPoint, focusedCuboidBoundingBox)) {
      return new TranslationDragHandler(
        this.axis,
        this.getPanelDirections(),
        event,
        this.injector,
      );
    } else if (isOverRotation(containerPoint, focusedCuboidBoundingBox)) {
      return new RotationDragHandler(this.axis, this.injector, event);
    } else {
      return undefined;
    }
  }

  private getPanelDirections(): Point2D {
    assertDefined(this.axis);

    switch (this.axis) {
      case 0:
        return [-1, 2];
      case 1:
        return [-0, 2];
      case 2:
        return [-0, -1];
    }
  }

  private getCamera() {
    assertDefined(this.axis);
    return this.editPanelCamerasService.getCameraByAxis(this.axis);
  }
}
